import { useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { SessionProvider, useSession } from './lib/session'
import { PlayerProvider, usePlayer } from './lib/player'
import { ClipQueueProvider } from './lib/clipQueue'
import { PENDING_INVITE_KEY } from './lib/invite'
import { Shell } from './components/Shell'
import { Spinner } from './components/ui'
import DevPersonaSwitcher from './components/DevPersonaSwitcher'
import UpdateBanner from './components/UpdateBanner'
import MfaChallenge from './components/MfaChallenge'

import Auth from './routes/Auth'
import Welcome from './routes/Welcome'
import RightsInfo from './routes/RightsInfo'
import Join from './routes/Join'
import DeleteConfirm from './routes/DeleteConfirm'
import Me from './routes/Me'
import Account from './routes/Account'
import Catalog from './routes/Catalog'
import Plans from './routes/Plans'
import Notifications from './routes/Notifications'
import ChurchSignup from './routes/admin/ChurchSignup'
import People from './routes/admin/People'
import Process from './routes/admin/Process'
import ChurchPageEditor from './routes/admin/Profile'
import Moderation from './routes/admin/Moderation'
import Endorsements from './routes/admin/Endorsements'
import EndorsementReview from './routes/admin/EndorsementReview'
import NewSong from './routes/songwriter/NewSong'
import CollectiveClip from './routes/songwriter/CollectiveClip'
import SyncLyrics from './routes/songwriter/SyncLyrics'
import Drafts from './routes/songwriter/Drafts'
import Create from './routes/Create'
import InviteRedeem from './routes/InviteRedeem'
import Home from './routes/Home'
import Feedback from './routes/Feedback'
import SupportInbox from './routes/admin/Feedback'
import Requests from './routes/admin/Requests'
import Business from './routes/admin/Business'
import Policy from './routes/Policy'
import Circles from './routes/Circles'
import Collective from './routes/Collective'
import SongRoom from './routes/SongRoom'
import WriterProfile from './routes/WriterProfile'
import ChurchProfile from './routes/ChurchProfile'
import Sharing from './routes/songwriter/Sharing'
import Vault from './routes/songwriter/Vault'
import Learn from './routes/songwriter/Learn'
import Sheet from './routes/songwriter/Sheet'
import Submit from './routes/songwriter/Submit'
import Endorse from './routes/songwriter/Endorse'
import Licences from './routes/songwriter/Licences'
import CoSign from './routes/songwriter/CoSign'
import Inbox from './routes/reviewer/Inbox'
import Review from './routes/reviewer/Review'

function Gate() {
  const { loading, user, profile, approved, role, isPlatformOnly, needsMfaChallenge } = useSession()

  if (loading) return <Shell><Spinner /></Shell>
  if (!user) return <Auth />
  if (needsMfaChallenge) return <MfaChallenge />

  // Signup asks for nothing but a way in. The name and the lane are collected
  // on Welcome instead, so anyone still missing either goes there first — a new
  // account, or one that predates the question.
  if (!profile?.first_name) return <Navigate to="/welcome" replace />
  if (!user.user_metadata?.lane_set) return <Navigate to="/welcome" replace />

  // No church? Still a songwriter. A church verifies a writer and endorses
  // their songs — it was never meant to be the door they have to get through
  // before they can write anything at all.
  // Platform staff before the seat checks below, because they have no seat to
  // check — and after the profile checks above, because staff still need a
  // name. The business dashboard is where staff land; moderation is one of
  // several platform surfaces.
  if (isPlatformOnly) return <Navigate to="/admin/platform" replace />

  if (role === 'reviewer') return <Navigate to="/inbox" replace />
  if (role === 'admin') return <Navigate to="/admin/people" replace />

  // A code picked up mid-signup — saved by InviteRedeem before sending them to
  // Welcome — gets first claim on where they land, ahead of the lane. It is
  // cleared the moment InviteRedeem finishes with it, so this only fires once.
  const pendingInvite = localStorage.getItem(PENDING_INVITE_KEY)
  if (pendingInvite) return <Navigate to={`/i/${pendingInvite}`} replace />

  // No church has verified them yet, so honour the lane they picked at signup.
  // It rides in user metadata, which the client can write — so it chooses a
  // screen and nothing more. church_members is still the only thing that grants
  // anything, and every route below has its own guard regardless.
  if (!approved.length) {
    const lane = user.user_metadata?.access_type
    if (lane === 'church') return <Navigate to="/church/new" replace />
    if (lane === 'industry') return <Navigate to="/welcome" replace />
  }
  return <Navigate to="/home" replace />
}

/** Route guard: only the named roles get through. */
function Only({ roles, children }) {
  const { loading, user, role, needsMfaChallenge } = useSession()
  if (loading) return <Shell><Spinner /></Shell>
  if (!user) return <Navigate to="/" replace />
  if (needsMfaChallenge) return <MfaChallenge />
  if (roles && !roles.includes(role)) return <Navigate to="/" replace />
  return children
}

/**
 * Route guard for platform staff. Separate from <Only> because that one reads
 * the church seat, and a superadmin is not a member of anything — they run the
 * platform. Sends anyone else back to Gate rather than showing a refusal: a
 * screen that says "you may not see this" confirms the screen exists.
 */
function Platform({ children }) {
  const { loading, user, isSuperadmin, needsMfaChallenge } = useSession()
  if (loading) return <Shell><Spinner /></Shell>
  if (!user || !isSuperadmin) return <Navigate to="/" replace />
  if (needsMfaChallenge) return <MfaChallenge />
  return children
}

/**
 * Renders nothing — stops the mini player surviving a sign-out. The song
 * itself keeps playing (the <audio> element outlives navigation on purpose,
 * see PlayerProvider), so without this the bar sits over the signed-out
 * landing screen still going, playing a song for whoever is now looking at
 * it. Watches `user` rather than intercepting the signOut() call, so it
 * catches every way a session ends — an expired token, not just the button.
 *
 * Has to live where both contexts are in scope: SessionProvider wraps
 * PlayerProvider in the tree below, so this needs to render as a descendant
 * of both, not inside either provider's own body.
 */
function PlayerSignOutSync() {
  const { user } = useSession()
  const { track, close } = usePlayer()
  const hadUser = useRef(false)
  useEffect(() => {
    if (hadUser.current && !user && track) close()
    hadUser.current = !!user
  }, [user, track, close])
  return null
}

export default function App() {
  return (
    <SessionProvider>
      {/* Above the router so navigating must not
          unmount the audio element, or playback stops the moment you leave the
          screen you started it from. */}
      <PlayerProvider>
      {/* Same reason as PlayerProvider directly above: preparing a clip takes
          twenty seconds and must survive the writer navigating away. */}
      <ClipQueueProvider>
      <PlayerSignOutSync />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Gate />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/rights" element={<Only><RightsInfo /></Only>} />
          <Route path="/join" element={<Join />} />
          <Route path="/account/delete-confirm" element={<DeleteConfirm />} />
          <Route path="/church/new" element={<ChurchSignup />} />
          {/* Has to work logged-out — same reasoning as /welcome and /join. */}
          <Route path="/i/:code" element={<InviteRedeem />} />

          {/* Church admin — people and setup only. No song review route exists. */}
          <Route path="/admin/people" element={<Only roles={['admin']}><People /></Only>} />
          <Route path="/admin/process" element={<Only roles={['admin']}><Process /></Only>} />
          {/* The church's outward-facing page, from the inside. Admin only —
              curating what the church shows off is setup, not review. */}
          <Route path="/admin/profile" element={<Only roles={['admin']}><ChurchPageEditor /></Only>} />
          {/* The church's side of the licence terms its writers set. Reviewers
              see it too — they are the ones who notice a renewal coming. */}
          <Route path="/admin/endorsements" element={<Only roles={['admin', 'reviewer']}><Endorsements /></Only>} />
          {/* The tile-by-tile Congregational/External Use decision for one
              proposed licence. A sibling of /admin/endorsements, not a child
              route on it, since it has its own back stack across steps. */}
          <Route path="/admin/endorsements/:id/review/:step" element={<Only roles={['admin', 'reviewer']}><EndorsementReview /></Only>} />

          {/* Platform. The business dashboard is the staff landing screen. */}
          <Route path="/admin/platform" element={<Platform><Business /></Platform>} />
          {/* What the three moderators have decided, in one place. */}
          <Route path="/admin/moderation" element={<Platform><Moderation /></Platform>} />
          {/* The other half of /support/:kind. Same <Platform> gate — a report
              carries someone's email and whatever they were doing at the time,
              which is staff-only for the same reasons moderation is. */}
          <Route path="/admin/feedback" element={<Platform><SupportInbox /></Platform>} />
          {/* What writers have asked Beseter/SPM to do. Written from the
              writer's catalogue; this is the half that reads it. */}
          <Route path="/admin/requests" element={<Platform><Requests /></Platform>} />

          {/* The browsable feed — other writers' songs, opened out to you. */}
          <Route path="/home" element={<Only><Home /></Only>} />

          {/* Songwriter. Staff write songs too, so they reach these as well.
              The list itself lives on /me now; /songs is a plain redirect so
              every existing "back to your songs" link still lands somewhere
              real, and only the exact path redirects — /songs/new and
              /songs/:id/* are unaffected. */}
          <Route path="/songs" element={<Navigate to="/me" replace />} />
          <Route path="/create" element={<Only><Create /></Only>} />
          {/* An idea, not a submission: this creates a song at Collective reach
              with a clip and nothing else, and never enters the church flow. */}
          <Route path="/create/clip" element={<Only><CollectiveClip /></Only>} />
          <Route path="/songs/new" element={<Only><NewSong /></Only>} />
          {/* Before /songs/:id/:step, and a sibling of it rather than a child,
              so "drafts" is never read as a song id. */}
          <Route path="/drafts" element={<Only><Drafts /></Only>} />
          {/* Also a sibling of /songs/:id/:step, and declared before it, so
              "endorse" is never read as a step number. */}
          <Route path="/songs/:id/endorse/:step" element={<Only><Endorse /></Only>} />
          <Route path="/licences" element={<Only><Licences /></Only>} />
          <Route path="/me" element={<Only><Me /></Only>} />
          <Route path="/account" element={<Only><Account /></Only>} />
          <Route path="/catalog" element={<Only><Catalog /></Only>} />
          <Route path="/plans" element={<Only><Plans /></Only>} />
          <Route path="/notifications" element={<Only><Notifications /></Only>} />
          <Route path="/profile" element={<Navigate to="/me" replace />} />

          {/* Support and legal. :kind is validated by the components against a
              fixed map, so an unknown value falls back rather than 404s. */}
          <Route path="/support/:kind" element={<Only><Feedback /></Only>} />
          <Route path="/legal/:kind" element={<Only><Policy /></Only>} />

          {/* The collective side. Both are RLS-shaped: they ask for everything
              and get back only what can_reach_song() lets through, so no client
              filter is load-bearing. */}
          <Route path="/collective" element={<Only><Collective /></Only>} />

          <Route path="/collective/:id" element={<Only><SongRoom /></Only>} />
          <Route path="/writer/:id" element={<Only><WriterProfile /></Only>} />
          {/* Declared after /church/new above, so "new" is never read as an id. */}
          <Route path="/church/:id" element={<Only><ChurchProfile /></Only>} />
          <Route path="/circles" element={<Only><Circles /></Only>} />
          <Route path="/songs/:id/sign" element={<Only><CoSign /></Only>} />
          {/* Both before the :step catch-all, or they're read as wizard steps.
              Learn is not owner-only: anyone the song's reach admits can open
              the chart, which is the whole point of allowing a cover. */}
          <Route path="/songs/:id/share" element={<Only><Sharing /></Only>} />
          {/* Before /songs/:id/:step, or "sync" is read as a wizard step. */}
          <Route path="/songs/:id/sync" element={<Only><SyncLyrics /></Only>} />
          <Route path="/songs/:id/takes" element={<Only><Vault /></Only>} />
          <Route path="/songs/:id/learn" element={<Only><Learn /></Only>} />
          <Route path="/songs/:id/sheet" element={<Only><Sheet /></Only>} />
          <Route path="/songs/:id/:step" element={<Only><Submit /></Only>} />

          {/* Reviewer */}
          <Route path="/inbox" element={<Only roles={['reviewer']}><Inbox /></Only>} />
          <Route path="/review/:id" element={<Only roles={['reviewer']}><Review /></Only>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ClipQueueProvider>
      </PlayerProvider>
      {/* Local dev only — import.meta.env.DEV is statically false in a
          production build, so this never renders outside `npm run dev`. */}
      {import.meta.env.DEV && <DevPersonaSwitcher />}
      <UpdateBanner />
    </SessionProvider>
  )
}
